import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastState {
  toasts: Toast[];
}

const initialState: ToastState = {
  toasts: [],
};

const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    addToast: (state, action: PayloadAction<Omit<Toast, 'id'>>) => {
      const id = Date.now().toString();
      const toast: Toast = {
        id,
        duration: 6000, // Default 6 seconds
        ...action.payload,
      };
      state.toasts.push(toast);
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload);
    },
    clearToasts: (state) => {
      state.toasts = [];
    },
    // Convenience actions for different toast types
    showSuccess: (state, action: PayloadAction<{ message: string; title?: string; duration?: number }>) => {
      const id = Date.now().toString();
      state.toasts.push({
        id,
        type: 'success',
        ...action.payload,
        duration: action.payload.duration || 4000,
      });
    },
    showError: (state, action: PayloadAction<{ message: string; title?: string; duration?: number }>) => {
      const id = Date.now().toString();
      state.toasts.push({
        id,
        type: 'error',
        ...action.payload,
        duration: action.payload.duration || 8000, // Errors stay longer
      });
    },
    showWarning: (state, action: PayloadAction<{ message: string; title?: string; duration?: number }>) => {
      const id = Date.now().toString();
      state.toasts.push({
        id,
        type: 'warning',
        ...action.payload,
        duration: action.payload.duration || 5000,
      });
    },
    showInfo: (state, action: PayloadAction<{ message: string; title?: string; duration?: number }>) => {
      const id = Date.now().toString();
      state.toasts.push({
        id,
        type: 'info',
        ...action.payload,
        duration: action.payload.duration || 4000,
      });
    },
  },
});

export const {
  addToast,
  removeToast,
  clearToasts,
  showSuccess,
  showError,
  showWarning,
  showInfo,
} = toastSlice.actions;

export default toastSlice.reducer;
